import { useEffect, useState, useRef } from "react";
import {
  Search,
  Plus,
  FileText,
  Eye,
  Download,
  XCircle,
  ChevronLeft,
  ChevronRight,
  Filter,
  AlertCircle,
} from "lucide-react";
import creditNoteService from "../services/creditNoteService";
import CreateCreditNoteModal from "../components/CreateCreditNoteModal";
import CreditNoteDetailModal from "../components/CreditNoteDetailModal";
import Swal from "sweetalert2";

export default function CreditNotes() {
  const [creditNotes, setCreditNotes] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedCreditNote, setSelectedCreditNote] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const searchRef = useRef(null);

  // ✅ ESTADOS DE PAGINACIÓN
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [from, setFrom] = useState(0);
  const [to, setTo] = useState(0);

  // ✅ ESTADOS DE FILTROS
  const [filterEstadoDte, setFilterEstadoDte] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  // ✅ ESTADOS DE ESTADÍSTICAS
  const [stats, setStats] = useState({
    total: 0,
    validadas: 0,
    pendientes: 0,
    rechazadas: 0,
    activas: 0,
    canceladas: 0,
    monto_total: 0,
  });

  // ✅ Cargar notas de crédito
  const fetchCreditNotes = async (page = 1, searchTerm = search) => {
    try {
      setLoading(true);
      const params = {
        page,
        search: searchTerm,
        ...(filterEstadoDte && { estado_dte: filterEstadoDte }),
        ...(filterStatus && { status: filterStatus }),
        ...(filterFrom && { from: filterFrom }),
        ...(filterTo && { to: filterTo }),
      };

      const res = await creditNoteService.getAll(params);

      if (res.data) {
        setCreditNotes(res.data);
        setCurrentPage(res.current_page);
        setTotalPages(res.last_page);
        setTotal(res.total);
        setFrom(res.from);
        setTo(res.to);
      } else {
        setCreditNotes(Array.isArray(res) ? res : []);
      }
    } catch (error) {
      console.error("Error cargando notas de crédito:", error);
      Swal.fire("Error", "No se pudieron cargar las notas de crédito", "error");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Cargar estadísticas
  const fetchStats = async () => {
    try {
      const params = {};
      if (filterFrom) params.from = filterFrom;
      if (filterTo) params.to = filterTo;

      const res = await creditNoteService.getStats(params);
      setStats(res);
    } catch (error) {
      console.error("Error cargando estadísticas:", error);
      // No mostrar alerta, solo usar valores por defecto
      setStats({
        total: 0,
        validadas: 0,
        pendientes: 0,
        rechazadas: 0,
        activas: 0,
        canceladas: 0,
        monto_total: 0,
      });
    }
  };

  useEffect(() => {
    fetchCreditNotes();
    // Comentar stats temporalmente hasta que haya datos
    // fetchStats();

    // Autofocus en el buscador
    setTimeout(() => {
      searchRef.current?.focus();
    }, 100);
  }, []);

  // ✅ Recargar cuando cambien los filtros
  useEffect(() => {
    if (currentPage === 1) {
      fetchCreditNotes(1, search);
    } else {
      setCurrentPage(1);
    }
    // Comentar stats temporalmente
    // fetchStats();
  }, [filterEstadoDte, filterStatus, filterFrom, filterTo]);

  // ✅ Función para cambiar página
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      fetchCreditNotes(newPage, search);
    }
  };

  // ✅ Manejar búsqueda con debounce
  const handleSearch = (value) => {
    setSearch(value);

    clearTimeout(window.searchTimeout);

    window.searchTimeout = setTimeout(() => {
      setCurrentPage(1);
      fetchCreditNotes(1, value);
    }, 500);
  };

  // ✅ Ver detalle de nota de crédito
  const handleViewDetail = async (creditNote) => {
    try {
      const res = await creditNoteService.getById(creditNote.id);
      setSelectedCreditNote(res);
      setShowDetailModal(true);
    } catch (error) {
      console.error("Error cargando detalle:", error);
      Swal.fire("Error", "No se pudo cargar el detalle de la nota", "error");
    }
  };

  // ✅ Descargar PDF
  const handleDownloadPdf = async (creditNote) => {
    try {
      await creditNoteService.downloadPdf(creditNote.id, creditNote.numero_control);
      Swal.fire({
        icon: "success",
        title: "Descarga iniciada",
        text: "El PDF se está descargando",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Error descargando PDF:", error);
      Swal.fire("Error", "No se pudo descargar el PDF", "error");
    }
  };

  // ✅ Cancelar nota de crédito
  const handleCancel = async (creditNote) => {
    const res = await Swal.fire({
      title: "¿Cancelar Nota de Crédito?",
      html: `
        <p style="margin-bottom: 12px;">Se cancelará la nota de crédito:</p>
        <p style="margin-bottom: 8px;"><strong>Número de Control:</strong> ${creditNote.numero_control}</p>
        <p style="margin-bottom: 8px;"><strong>Cliente:</strong> ${creditNote.sale?.client?.nombre || "N/A"}</p>
        <p style="margin-bottom: 8px;"><strong>Monto:</strong> $${Number(creditNote.amount).toFixed(2)}</p>
        <div style="margin-top: 16px; padding: 12px; background: #fef3c7; border-radius: 8px; border-left: 4px solid #f59e0b;">
          <p style="margin: 0; font-size: 13px; color: #92400e;">
            <strong>⚠️ Advertencia:</strong> Esta acción no puede revertirse. 
            La nota será marcada como cancelada pero seguirá existiendo en el sistema.
          </p>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Sí, cancelar",
      cancelButtonText: "No, mantener",
    });

    if (res.isConfirmed) {
      try {
        await creditNoteService.cancel(creditNote.id);
        Swal.fire("Cancelada", "Nota de crédito cancelada correctamente", "success");
        fetchCreditNotes(currentPage, search);
        fetchStats();
      } catch (error) {
        console.error("Error cancelando nota:", error);
        Swal.fire(
          "Error",
          error.response?.data?.error || "No se pudo cancelar la nota de crédito",
          "error"
        );
      }
    }
  };

  // ✅ Limpiar filtros
  const handleClearFilters = () => {
    setFilterEstadoDte("");
    setFilterStatus("");
    setFilterFrom("");
    setFilterTo("");
  };

  return (
    <div style={{ padding: "24px", maxWidth: "1400px", margin: "0 auto" }}>
      {/* CARD ESTADÍSTICAS */}
      <div
        style={{
          display: "none",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: "16px",
          marginBottom: "24px",
        }}
      >
        <div
          className="card"
          style={{
            padding: "16px",
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "#fff",
          }}
        >
          <div style={{ fontSize: "13px", opacity: 0.9, marginBottom: "4px" }}>
            Total Notas
          </div>
          <div style={{ fontSize: "28px", fontWeight: "700" }}>{stats.total}</div>
        </div>

        <div className="card" style={{ padding: "16px", background: "#f0fdf4" }}>
          <div style={{ fontSize: "13px", color: "#166534", marginBottom: "4px" }}>
            Validadas
          </div>
          <div style={{ fontSize: "28px", fontWeight: "700", color: "#16a34a" }}>
            {stats.validadas}
          </div>
        </div>

        <div className="card" style={{ padding: "16px", background: "#fef3c7" }}>
          <div style={{ fontSize: "13px", color: "#92400e", marginBottom: "4px" }}>
            Pendientes
          </div>
          <div style={{ fontSize: "28px", fontWeight: "700", color: "#f59e0b" }}>
            {stats.pendientes}
          </div>
        </div>

        <div className="card" style={{ padding: "16px", background: "#fee2e2" }}>
          <div style={{ fontSize: "13px", color: "#991b1b", marginBottom: "4px" }}>
            Rechazadas
          </div>
          <div style={{ fontSize: "28px", fontWeight: "700", color: "#dc2626" }}>
            {stats.rechazadas}
          </div>
        </div>

        <div className="card" style={{ padding: "16px", background: "#f0f9ff" }}>
          <div style={{ fontSize: "13px", color: "#0c4a6e", marginBottom: "4px" }}>
            Monto Total
          </div>
          <div style={{ fontSize: "24px", fontWeight: "700", color: "#0284c7" }}>
            ${Number(stats.monto_total).toFixed(2)}
          </div>
        </div>
      </div>

      {/* CARD PRINCIPAL */}
      <div className="card">
        {/* HEADER */}
        <div className="card-header">
          <div className="d-flex align-items-center gap-2">
            <FileText size={28} color="#667eea" />
            <h1 className="card-title">Notas de Crédito (DTE 05)</h1>
          </div>

          <button
            className="btn btn-success"
            onClick={() => setShowCreateModal(true)}
          >
            <Plus size={18} />
            Nueva Nota de Crédito
          </button>
        </div>

        {/* BODY */}
        <div className="card-body">
          {/* BUSCADOR Y FILTROS */}
          <div style={{ marginBottom: "20px" }}>
            <div className="form-group" style={{ position: "relative", marginBottom: "12px" }}>
              <div style={{ position: "relative" }}>
                <Search
                  size={18}
                  style={{
                    position: "absolute",
                    left: "16px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#a0aec0",
                  }}
                />
                <input
                  type="text"
                  className="form-control"
                  placeholder="Buscar por cliente, número de control o código de generación..."
                  value={search}
                  onChange={(e) => handleSearch(e.target.value)}
                  ref={searchRef}
                  style={{ paddingLeft: "44px", paddingRight: "44px" }}
                />
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => setShowFilters(!showFilters)}
                  style={{
                    position: "absolute",
                    right: "8px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    padding: "8px 12px",
                  }}
                  title="Mostrar/Ocultar filtros"
                >
                  <Filter size={16} />
                </button>
              </div>
            </div>

            {/* PANEL DE FILTROS */}
            {showFilters && (
              <div
                style={{
                  padding: "16px",
                  background: "#f7fafc",
                  borderRadius: "10px",
                  marginBottom: "16px",
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                    gap: "12px",
                  }}
                >
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label" style={{ fontSize: "13px", marginBottom: "4px" }}>
                      Estado DTE
                    </label>
                    <select
                      className="form-control"
                      value={filterEstadoDte}
                      onChange={(e) => setFilterEstadoDte(e.target.value)}
                    >
                      <option value="">Todos</option>
                      <option value="VALIDADO">Validado</option>
                      <option value="PENDIENTE">Pendiente</option>
                      <option value="RECHAZADO">Rechazado</option>
                    </select>
                  </div>

                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label" style={{ fontSize: "13px", marginBottom: "4px" }}>
                      Estado
                    </label>
                    <select
                      className="form-control"
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                    >
                      <option value="">Todos</option>
                      <option value="active">Activa</option>
                      <option value="cancelled">Cancelada</option>
                    </select>
                  </div>

                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label" style={{ fontSize: "13px", marginBottom: "4px" }}>
                      Desde
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      value={filterFrom}
                      onChange={(e) => setFilterFrom(e.target.value)}
                    />
                  </div>

                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label" style={{ fontSize: "13px", marginBottom: "4px" }}>
                      Hasta
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      value={filterTo}
                      onChange={(e) => setFilterTo(e.target.value)}
                    />
                  </div>
                </div>

                {(filterEstadoDte || filterStatus || filterFrom || filterTo) && (
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={handleClearFilters}
                    style={{ marginTop: "12px" }}
                  >
                    Limpiar Filtros
                  </button>
                )}
              </div>
            )}
          </div>

          {/* TABLA */}
          {loading ? (
            <div className="text-center" style={{ padding: "40px" }}>
              <div className="spinner" style={{ margin: "0 auto" }}></div>
              <p style={{ marginTop: "16px", color: "#718096" }}>
                Cargando notas de crédito...
              </p>
            </div>
          ) : (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th style={{ width: "300px" }}>Código de Generación</th>
                    <th>Cliente</th>
                    <th style={{ width: "120px" }}>Fecha</th>
                    <th style={{ textAlign: "right", width: "120px" }}>Monto</th>
                    <th style={{ textAlign: "center", width: "120px" }}>Estado DTE</th>
                    <th style={{ textAlign: "center", width: "100px" }}>Estado</th>
                    <th style={{ textAlign: "center", width: "220px" }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {creditNotes.length === 0 ? (
                    <tr>
                      <td colSpan="7">
                        <div className="text-center" style={{ padding: "40px 20px" }}>
                          <FileText size={48} color="#cbd5e0" style={{ marginBottom: "16px" }} />
                          <p style={{ color: "#a0aec0", fontSize: "16px", margin: 0 }}>
                            {search || filterEstadoDte || filterStatus || filterFrom || filterTo
                              ? "No se encontraron notas de crédito con esos filtros"
                              : "No hay notas de crédito registradas"}
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    creditNotes.map((cn) => (
                      <tr key={cn.id}>
                        <td>
                          <strong 
                            style={{ 
                              fontWeight: 600, 
                              fontSize: "11px",
                              wordBreak: "break-all",
                              fontFamily: "monospace",
                              display: "block",
                              maxWidth: "300px"
                            }}
                          >
                            {cn.codigo_generacion}
                          </strong>
                        </td>
                        <td>{cn.sale?.client?.nombre || "N/A"}</td>
                        <td>
                          {new Date(cn.fecha_emision).toLocaleDateString("es-SV", {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                          })}
                        </td>
                        <td style={{ textAlign: "right", fontWeight: 700, color: "#dc2626" }}>
                          ${Number(cn.amount).toFixed(2)}
                        </td>
                        <td style={{ textAlign: "center" }}>
                          {cn.estado_dte === "VALIDADO" && (
                            <span
                              style={{
                                padding: "4px 12px",
                                background: "#d1fae5",
                                color: "#065f46",
                                borderRadius: "6px",
                                fontSize: "12px",
                                fontWeight: 600,
                              }}
                            >
                              ✓ Validado
                            </span>
                          )}
                          {cn.estado_dte === "PENDIENTE" && (
                            <span
                              style={{
                                padding: "4px 12px",
                                background: "#fef3c7",
                                color: "#92400e",
                                borderRadius: "6px",
                                fontSize: "12px",
                                fontWeight: 600,
                              }}
                            >
                              ⏳ Pendiente
                            </span>
                          )}
                          {cn.estado_dte === "RECHAZADO" && (
                            <span
                              style={{
                                padding: "4px 12px",
                                background: "#fee2e2",
                                color: "#991b1b",
                                borderRadius: "6px",
                                fontSize: "12px",
                                fontWeight: 600,
                              }}
                            >
                              ✕ Rechazado
                            </span>
                          )}
                        </td>
                        <td style={{ textAlign: "center" }}>
                          {cn.status === "active" ? (
                            <span
                              style={{
                                padding: "4px 12px",
                                background: "#dbeafe",
                                color: "#1e40af",
                                borderRadius: "6px",
                                fontSize: "12px",
                                fontWeight: 600,
                              }}
                            >
                              Activa
                            </span>
                          ) : (
                            <span
                              style={{
                                padding: "4px 12px",
                                background: "#f3f4f6",
                                color: "#6b7280",
                                borderRadius: "6px",
                                fontSize: "12px",
                                fontWeight: 600,
                              }}
                            >
                              Cancelada
                            </span>
                          )}
                        </td>
                        <td>
                          <div className="d-flex gap-2" style={{ justifyContent: "center" }}>
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() => handleViewDetail(cn)}
                              title="Ver detalle"
                            >
                              <Eye size={16} />
                            </button>
                            <button
                              className="btn btn-success btn-sm"
                              onClick={() => handleDownloadPdf(cn)}
                              title="Descargar PDF"
                            >
                              <Download size={16} />
                            </button>
                            {cn.status === "active" && (
                              <button
                                className="btn btn-danger btn-sm"
                                onClick={() => handleCancel(cn)}
                                title="Cancelar nota"
                              >
                                <XCircle size={16} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* PAGINACIÓN */}
          {totalPages > 1 && (
            <div
              style={{
                marginTop: "24px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <ChevronLeft size={16} />
              </button>

              <span style={{ padding: "6px 12px", color: "#718096", fontSize: "14px" }}>
                Página {currentPage} de {totalPages}
              </span>

              <button
                className="btn btn-secondary btn-sm"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                <ChevronRight size={16} />
              </button>
            </div>
          )}

          {/* RESUMEN */}
          {creditNotes.length > 0 && (
            <div
              style={{
                marginTop: "16px",
                padding: "16px",
                background: "#f7fafc",
                borderRadius: "10px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "16px",
              }}
            >
              <span style={{ color: "#718096", fontSize: "14px" }}>
                Mostrando {from} - {to} de {total} notas de crédito
              </span>
            </div>
          )}
        </div>
      </div>

      {/* MODALES */}
      {showCreateModal && (
        <CreateCreditNoteModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            fetchCreditNotes(currentPage, search);
            // fetchStats(); // Comentado temporalmente
          }}
        />
      )}

      {showDetailModal && selectedCreditNote && (
        <CreditNoteDetailModal
          creditNote={selectedCreditNote}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedCreditNote(null);
          }}
          onDownloadPdf={handleDownloadPdf}
        />
      )}
    </div>
  );
}